import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Chip,
  Avatar,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  Stack,
  Divider,
  CircularProgress,
  Alert,
  Snackbar
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import AddIcon from '@mui/icons-material/Add';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EventIcon from '@mui/icons-material/Event';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import PeopleIcon from '@mui/icons-material/People';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import PendingIcon from '@mui/icons-material/Pending';
import { useAuth } from '../context/AuthContext';

const getStatusColor = (status) => {
  switch (status.toLowerCase()) {
    case 'completed': return 'success';
    case 'ongoing': return 'warning';
    case 'upcoming': return 'info';
    default: return 'default';
  }
};

const getStatusIcon = (status) => {
  switch (status.toLowerCase()) {
    case 'completed': return <CheckCircleIcon fontSize="small" />;
    case 'ongoing': return <PlayCircleIcon fontSize="small" />;
    case 'upcoming': return <PendingIcon fontSize="small" />;
    default: return <EventIcon fontSize="small" />;
  }
};

export default function Events() {
  // State management
  const [events, setEvents] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [formData, setFormData] = useState({
    name: '',
    startDate: null,
    endDate: null,
    location: '',
    status: 'Upcoming',
    branchId: '',
    description: '',
    estimatedAttendees: ''
  });
  const { user } = useAuth();


  // API Functions
  useEffect(() => {
    fetchEvents();
    fetchBranches();
    
  }, []);

  useEffect(()=>{
console.log(events);
  },[events])

  const fetchBranches = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/branches', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBranches(response.data);
    } catch (error) {
      console.error('Failed to fetch branches:', error);
      showSnackbar('Failed to load branches', 'error');
    }
  };

  const fetchEvents = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/events', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEvents(response.data);
    } catch (error) {
      console.error('Failed to fetch events:', error);
      showSnackbar('Failed to load events', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const handleOpenDialog = (event = null) => {
    if (event) {
      setEditingEvent(event);
      setFormData({
        name: event.name,
        startDate: new Date(event.startDate),
        endDate: new Date(event.endDate),
        location: event.location,
        status: event.status,
        branchId: event.branchId || '',
        description: event.description,
        estimatedAttendees: event.estimatedAttendees
      });
    } else {
      setEditingEvent(null);
      setFormData({
        name: '',
        startDate: null,
        endDate: null,
        location: '',
        status: 'Upcoming',
        branchId: '',
        description: '',
        estimatedAttendees: ''
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingEvent(null);
    setSaving(false);
  };

  const handleSaveEvent = async () => {
    if (!formData.name || !formData.startDate || !formData.endDate || !formData.location) {
      showSnackbar('Please fill in all required fields', 'error');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const eventData = {
        ...formData,
        estimatedAttendees: parseInt(formData.estimatedAttendees) || 0
      };

      if (editingEvent) {
        // Update existing event
        await axios.put(`/api/events/${editingEvent._id || editingEvent.id}`, eventData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        showSnackbar('Event updated successfully');
      } else {
        // Create new event
        await axios.post('/api/events', eventData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        showSnackbar('Event created successfully');
      }
      
      fetchEvents(); // Refresh the list
      handleCloseDialog();
    } catch (error) {
      console.error('Failed to save event:', error);
      showSnackbar(
        error.response?.data?.error || 'Failed to save event', 
        'error'
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteEvent = async (eventId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`/api/events/${eventId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      showSnackbar('Event deleted successfully');
      fetchEvents(); // Refresh the list
      handleCloseMenu();
    } catch (error) {
      console.error('Failed to delete event:', error);
      showSnackbar(
        error.response?.data?.error || 'Failed to delete event', 
        'error'
      );
    }
  };

  const handleMenuClick = (event, eventData) => {
    setAnchorEl(event.currentTarget);
    setSelectedEvent(eventData);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setSelectedEvent(null);
  };

  const formatDateRange = (start, end) => {
    const startDate = new Date(start);
    const endDate = new Date(end);
    const options = { month: 'short', day: 'numeric', year: 'numeric' };
    
    if (startDate.getFullYear() !== endDate.getFullYear()) {
      return `${startDate.toLocaleDateString('en-US', options)} - ${endDate.toLocaleDateString('en-US', options)}`;
    } else if (startDate.getMonth() !== endDate.getMonth()) {
      return `${startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${endDate.toLocaleDateString('en-US', options)}`;
    } else {
      return `${startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${endDate.getDate()}, ${endDate.getFullYear()}`;
    }
  };

  // Loading state
  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '400px' 
      }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ p: 3 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
            sx={{ borderRadius: 2, px: 3 }}
          >
            Add New Event
          </Button>
        </Box>

        {/* Events Grid */}
        {events.length === 0 ? (
          <Card sx={{ borderRadius: 3, textAlign: 'center', p: 6 }}>
            <EventIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
              No Events Found
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Create your first event to get started
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
              sx={{ borderRadius: 2 }}
            >
              Create Event
            </Button>
          </Card>
        ) : (
          <Grid container spacing={3}>
            {events.map((event) => (
              <Grid item xs={12} md={6} lg={4} key={event._id || event.id}>
                <Card sx={{ 
                  borderRadius: 3,
                  boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 8px 30px rgba(0,0,0,0.12)'
                  }
                }}>
                  <CardContent sx={{ p: 3 }}>
                    {/* Header with menu */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Avatar sx={{
                          bgcolor: getStatusColor(event.status) === 'success' ? '#22c55e' : 
                                   getStatusColor(event.status) === 'warning' ? '#f59e0b' :
                                   getStatusColor(event.status) === 'info' ? '#3b82f6' : '#6b7280',
                          width: 40,
                          height: 40
                        }}>
                          {getStatusIcon(event.status)}
                        </Avatar>
                        <Box>
                          <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.2 }}>
                            {event.name}
                          </Typography>
                          <Chip
                            label={event.status}
                            color={getStatusColor(event.status)}
                            size="small"
                            sx={{ mt: 0.5, fontWeight: 600 }}
                          />
                        </Box>
                      </Box>
                      <IconButton 
                        onClick={(e) => handleMenuClick(e, event)}
                        size="small"
                      >
                        <MoreVertIcon />
                      </IconButton>
                    </Box>

                    <Divider sx={{ my: 2 }} />

                    {/* Event Details */}
                    <Stack spacing={2}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <EventIcon color="action" fontSize="small" />
                        <Typography variant="body2" color="text.secondary">
                          {formatDateRange(event.startDate, event.endDate)}
                        </Typography>
                      </Box>
                      
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LocationOnIcon color="action" fontSize="small" />
                        <Typography variant="body2" color="text.secondary">
                          {event.location}
                        </Typography>
                      </Box>
                      {
                        user.role === "SuperAdmin" && 
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <AccountBalanceIcon color="action" fontSize="small" />
                          <Typography variant="body2" color="text.secondary">
                            {event.branchId.name}
                          </Typography>
                        </Box>
                      }
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}

        {/* Context Menu */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleCloseMenu}
          PaperProps={{
            sx: { borderRadius: 2, minWidth: 150 }
          }}
        >
          <MenuItem onClick={() => { handleOpenDialog(selectedEvent); handleCloseMenu(); }}>
            <EditIcon fontSize="small" sx={{ mr: 1 }} />
            Edit Event
          </MenuItem>
          <MenuItem 
            onClick={() => handleDeleteEvent(selectedEvent?._id || selectedEvent?.id)}
            sx={{ color: 'error.main' }}
          >
            <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
            Delete Event
          </MenuItem>
        </Menu>

        {/* Add/Edit Event Dialog */}
        <Dialog 
          open={openDialog} 
          onClose={handleCloseDialog}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: { borderRadius: 3 }
          }}
        >
          <DialogTitle sx={{ pb: 1 }}>
            <Typography variant="h5" fontWeight={700}>
              {editingEvent ? 'Edit Event' : 'Add New Event'}
            </Typography>
          </DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  label="Event Name *"
                  fullWidth
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  variant="outlined"
                  error={!formData.name}
                  helperText={!formData.name ? 'Event name is required' : ''}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <DatePicker
                  label="Start Date *"
                  value={formData.startDate}
                  onChange={(date) => setFormData({ ...formData, startDate: date })}
                  renderInput={(params) => (
                    <TextField 
                      {...params} 
                      fullWidth 
                      error={!formData.startDate}
                      helperText={!formData.startDate ? 'Start date is required' : ''}
                    />
                  )}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <DatePicker
                  label="End Date *"
                  value={formData.endDate}
                  onChange={(date) => setFormData({ ...formData, endDate: date })}
                  renderInput={(params) => (
                    <TextField 
                      {...params} 
                      fullWidth 
                      error={!formData.endDate}
                      helperText={!formData.endDate ? 'End date is required' : ''}
                    />
                  )}
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  label="Location *"
                  fullWidth
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  variant="outlined"
                  error={!formData.location}
                  helperText={!formData.location ? 'Location is required' : ''}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={formData.status}
                    label="Status"
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  >
                    <MenuItem value="Upcoming">Upcoming</MenuItem>
                    <MenuItem value="Ongoing">Ongoing</MenuItem>
                    <MenuItem value="Completed">Completed</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              { user.role === "SuperAdmin" &&
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Select Branch</InputLabel>
                    <Select
                      value={formData.branchId}
                      label="Select Branch"
                      onChange={(e) => setFormData({ ...formData, branchId: e.target.value })}
                    >
                      <MenuItem value="">
                        <em>None</em>
                      </MenuItem>
                      {branches.map((branch) => (
                        <MenuItem key={branch._id} value={branch._id}>
                          {branch.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              }
            </Grid>
          </DialogContent>
          <DialogActions sx={{ p: 3, pt: 1 }}>
            <Button onClick={handleCloseDialog} sx={{ borderRadius: 2 }}>
              Cancel
            </Button>
            <Button 
              variant="contained" 
              onClick={handleSaveEvent}
              disabled={saving}
              sx={{ borderRadius: 2, px: 3 }}
            >
              {saving ? (
                <CircularProgress size={20} sx={{ color: 'white' }} />
              ) : (
                editingEvent ? 'Update Event' : 'Create Event'
              )}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert 
            onClose={handleCloseSnackbar} 
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </LocalizationProvider>
  );
}
